import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';

import styled, { css } from 'styled-components';

import { getFontFamily } from 'services/FontService';
import { handleFinancialNumberValue } from 'services/NumberService';

export interface IInput extends IInputStyle {
  value: string;
  placeholder?: string;
  type: 'number' | 'text' | 'password' | 'email';
  name?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onEnter?: (value: string) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  max?: number;
  financialNumber?: boolean;
}

export interface IInputStyle {
  color?: string;
  disabled?: boolean;
  hasError?: boolean;
  hasWarning?: boolean;
}

export const Input = memo(
  ({
    value,
    name,
    onChange,
    onEnter,
    onFocus,
    onBlur,
    max,
    financialNumber,
    ...rest
  }: IInput) => {
    const onKeyDownHandler = useCallback(
      ({ key }: React.KeyboardEvent<HTMLInputElement>) => {
        if (key === 'Enter') {
          if (onEnter) onEnter(value);
        }
      },
      [value, onEnter],
    );

    const onFinancialNumberBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        const currentValue = event.target.value;
        const newValue = handleFinancialNumberValue(currentValue);

        if (newValue !== currentValue) {
          onChange({
            ...event,
            target: {
              ...event.target,
              value: newValue || '',
            },
          });
        }
      },
      [onChange],
    );

    const onBlurHandler:
      | React.FocusEventHandler<HTMLInputElement>
      | undefined = useMemo(() => {
      if (onBlur) {
        return onBlur;
      }

      if (financialNumber) {
        return onFinancialNumberBlur;
      }
    }, [financialNumber, onBlur, onFinancialNumberBlur]);

    return (
      <InputStyled
        value={value}
        name={name}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlurHandler}
        maxLength={max}
        onKeyDown={onKeyDownHandler}
        {...rest}
      />
    );
  },
);

const InputStyled = styled.input<IInputStyle>`
  width: 100%;
  padding: 11px 16px;
  box-sizing: border-box;
  background-color: var(--k-bg-ffffff);
  border-width: 1px;
  border-style: solid;
  ${({ hasError, hasWarning }) => {
    if (hasError) {
      return css`
        border-color: var(--k-border-f44336);
      `;
    }

    if (hasWarning) {
      return css`
        border-color: var(--k-border-f3bb1c);
      `;
    }

    return css`
      border-color: var(--k-border-ebeff2);
    `;
  }}
  color: ${({ color }) => color || 'var(--k-fg-263446)'};
  font-family: ${getFontFamily('regular')};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  cursor: pointer;
  outline: none;
  ${({ disabled }) => {
    if (disabled) {
      return css`
        color: var(--k-fg-263446);
        background-color: var(--k-bg-fafbfd);
        user-select: none;
        cursor: default;
      `;
    }
  }}

  &::placeholder {
    color: var(--k-fg-616e78);
    opacity: 1;
  }
`;
